import React, { useState, useEffect, useRef } from 'react'
import Image from '@atoms/Image/Image'
import { CarouselProps } from './Carousel.types'
import { DefaultLeftIcon, DefaultRightIcon } from './Carousel.partials'

const Carousel = ({
  autoplay = false,
  autoplayDuration = 5000,
  iconStyles = {
    strokeWidth: '10px',
    width: '40px'
  },
  control = {
    leftArrow: (
      <DefaultLeftIcon
        strokeWidth={iconStyles?.strokeWidth}
        width={iconStyles?.width}
      />
    ),
    rightArrow: (
      <DefaultRightIcon
        strokeWidth={iconStyles?.strokeWidth}
        width={iconStyles?.width}
      />
    )
  },
  hideArrows = false,
  hideControl = false,
  hideIndicators = false,
  pauseOnHover = false,
  productImages,
  transitionDuration = 'duration-700',
  isFullScreenMode = false,
  showThumbnails = false,
  isDarkMode = false,
  firstImageIsHighPriority = false
}: CarouselProps) => {
  // get duration ms from tailwind class as number
  const TRANSITION_DURATION =
    parseInt(transitionDuration.replace(/\D/g, ''), 10) || 700

  const DESKTOP_MIN_WIDTH_PX = 992
  const isDesktop =
    typeof window !== 'undefined' && window?.innerWidth >= DESKTOP_MIN_WIDTH_PX
  const MAX_IMAGES = 5

  const [imgIndex, setImgIndex] = useState<number>(1)
  const [noTransition, setNoTransition] = useState<boolean>(false)
  const [xSlidePos, setXSlidePos] = useState<number>(0)
  const [isSliding, setIsSliding] = useState<boolean>(false)

  const slideTimeout = useRef<ReturnType<typeof setTimeout>>(
    setTimeout(() => {})
  )
  const slicedImages =
    productImages?.length > 5
      ? productImages.slice(0, MAX_IMAGES)
      : productImages || []

  // clone last and first elements for the 'infinite' sliding effect
  const imgSet =
    slicedImages.length > 1
      ? [
          slicedImages[slicedImages.length - 1],
          ...slicedImages,
          slicedImages[0]
        ]
      : [...slicedImages]
  const lazyImgs = slicedImages.length > 1 ? [0, imgSet.length - 1] : []

  const shouldAutoplay = autoplay && slicedImages.length > 1

  const setAutoplay = (): void => {
    if (shouldAutoplay) {
      slideTimeout.current = setTimeout(() => {
        slideNext()
      }, autoplayDuration - (imgIndex === 1 ? TRANSITION_DURATION : 0))
    }
  }

  const pauseTimeout = (): void => {
    if (shouldAutoplay) {
      clearTimeout(slideTimeout?.current)
    }
  }

  const resumeTimeout = (): void => {
    if (shouldAutoplay) {
      setAutoplay()
    }
  }

  useEffect(() => {
    setAutoplay()
    return () => {
      clearTimeout(slideTimeout?.current)
    }
  }, [])

  const slideNext = () => {
    if (noTransition) {
      setNoTransition(false)
    }
    if (imgIndex + 1 !== imgSet.length) {
      setImgIndex(imgIndex + 1)
    }
    if (imgIndex === imgSet.length - 2) {
      setTimeout(() => {
        setNoTransition(true)
        setImgIndex(1)
      }, TRANSITION_DURATION)
    }
  }

  const slidePrev = () => {
    if (imgIndex - 1 !== -1) {
      setImgIndex(imgIndex - 1)
    }
    if (noTransition) {
      setNoTransition(false)
    }
    if (imgIndex === 1) {
      setTimeout(() => {
        setNoTransition(true)
        setImgIndex(imgSet.length - 2)
      }, TRANSITION_DURATION)
    }
  }

  const handleIndicatorClick = (indicatorIndex: number) => {
    setNoTransition(false)
    setImgIndex(indicatorIndex)
  }

  const handleSlideStart = (newXPos: number) => {
    setXSlidePos(newXPos)
    setIsSliding(true)
  }

  const handleSlide = (newXPos: number) => {
    if (xSlidePos > newXPos && isSliding) {
      slideNext()
    } else if (xSlidePos < newXPos && isSliding) {
      slidePrev()
    }
    setIsSliding(false)
  }

  useEffect(() => {
    clearTimeout(slideTimeout?.current)
    setAutoplay()
  }, [imgIndex])

  useEffect(() => {
    return () => {
      clearTimeout(slideTimeout?.current)
    }
  }, [])

  return (
    <div
      className={`relative top-0 overflow-hidden ${
        isFullScreenMode ? 'h-full' : 'h-fit'
      }`}
    >
      <div
        className={`relative w-full ${
          isFullScreenMode
            ? 'h-screen w-full md:h-screen md:max-h-[150vw]'
            : 'h-full pb-[100%]'
        } ${
          noTransition
            ? 'duration-none transition-none'
            : `transition-transform ${transitionDuration}`
        }`}
        style={{ transform: `translateX(calc(100% * ${-(imgIndex - 1)}))` }}
      >
        {imgSet.length > 0 &&
          imgSet.map((img, idx) => {
            const pImage = img.disBaseLink || img.link
            return (
              <div
                style={{
                  transform:
                    imgSet.length > 1
                      ? `translateX(calc(100% * ${idx - 1}))`
                      : ''
                }}
                key={idx}
                className={`absolute h-full w-full ${
                  !pImage || pImage.length === 0 ? 'bg-machine2-25' : ''
                }`}
                onTouchStart={(ev) =>
                  !isDesktop && handleSlideStart(ev.changedTouches[0].pageX)
                }
                onTouchMove={(ev) =>
                  !isDesktop && handleSlide(ev.changedTouches[0].pageX)
                }
                onMouseOver={() => isDesktop && pauseOnHover && pauseTimeout()}
                onMouseLeave={() =>
                  isDesktop && pauseOnHover && resumeTimeout()
                }
              >
                {pImage && pImage.length > 0 && (
                  <Image
                    className={`h-full w-full object-cover ${
                      isFullScreenMode && idx === 1
                        ? 'object-right'
                        : 'object-center'
                    }`}
                    src={pImage}
                    alt={img.alt || img.title}
                    ratio='1/2'
                    loading={lazyImgs.includes(idx) ? 'lazy' : 'eager'}
                    fetchPriority={
                      ((slicedImages.length > 1 && idx === 1) ||
                        slicedImages.length === 1) &&
                      firstImageIsHighPriority
                        ? 'high'
                        : 'auto'
                    }
                  />
                )}
              </div>
            )
          })}
      </div>
      {!hideControl && slicedImages.length > 1 && (
        <div
          className={`absolute bottom-0 flex h-[60px] w-full items-center transition-colors duration-500 ${
            isDarkMode ? 'fill-black stroke-black' : 'fill-white stroke-white'
          } ${hideArrows ? 'justify-center' : 'justify-between'} px-8`}
        >
          {!hideArrows && (
            <div
              className='flex max-w-[20px] cursor-pointer focus:outline-none active:outline-none md:max-w-[40px]'
              onClick={() => slidePrev()}
            >
              {control.leftArrow}
            </div>
          )}
          {!hideIndicators && (
            <div
              className={`relative flex h-full w-fit items-center justify-between focus:outline-none active:outline-none ${
                showThumbnails ? 'mb-24' : ''
              }`}
            >
              {Array.from({ length: imgSet.length }).map(
                (_, indicatorIndex) => (
                  <div
                    key={indicatorIndex}
                    onClick={() => handleIndicatorClick(indicatorIndex)}
                    className={`mx-1 my-auto flex h-full cursor-pointer flex-col
                ${
                  indicatorIndex === 0
                    ? 'pointer-events-none absolute right-0'
                    : ''
                } ${
                      indicatorIndex === imgSet.length - 1
                        ? 'pointer-events-none absolute left-0'
                        : ''
                    }
                `}
                  >
                    {showThumbnails ? (
                      imgSet?.[indicatorIndex] && (
                        <div className={'flex flex-col'}>
                          <div
                            className={`h-8 w-8 md:h-12 md:w-12 ${
                              indicatorIndex === imgIndex
                                ? 'opacity-90'
                                : 'opacity-25'
                            }`}
                          >
                            <Image
                              className={`h-full w-full object-cover ${
                                isFullScreenMode && indicatorIndex === 1
                                  ? 'object-right'
                                  : 'object-center'
                              }`}
                              src={
                                imgSet?.[indicatorIndex].disBaseLink ||
                                imgSet?.[indicatorIndex].link
                              }
                              alt={
                                imgSet?.[indicatorIndex].alt ||
                                imgSet?.[indicatorIndex].title
                              }
                              ratio='1/2'
                              loading='eager'
                            />
                          </div>
                          <div
                            className={`mt-0.5 h-0.5 w-full transition-colors duration-500 ${
                              isDarkMode ? 'bg-black' : 'bg-white'
                            } ${
                              indicatorIndex === imgIndex
                                ? 'visible'
                                : 'invisible'
                            }`}
                          />
                        </div>
                      )
                    ) : (
                      <div
                        className={`m-auto w-8 transition-colors duration-500 md:w-12 ${
                          isDarkMode ? 'bg-black' : 'bg-white'
                        } ${
                          indicatorIndex === imgIndex
                            ? 'h-[2px] md:h-[4px]'
                            : 'h-[1px] md:h-[2px]'
                        } `}
                      />
                    )}
                  </div>
                )
              )}
            </div>
          )}
          {!hideArrows && (
            <div
              className='flex max-w-[20px] cursor-pointer focus:outline-none active:outline-none md:max-w-[40px]'
              onClick={() => slideNext()}
            >
              {control.rightArrow}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Carousel
